/** @typedef {import('../entities/CareModelCohort.types').CareModelCohort} CareModelCohort */
/** @typedef {import('../entities/CareModelCohort.types').Client} Client */
/** @typedef {import('../entities/CareModelCohort.types').CohortFilters} CohortFilters */

/**
 * Sort cohorts by order.
 * @param {CareModelCohort[]} cohorts
 * @returns {CareModelCohort[]}
 */
export function sortCohortsByOrder(cohorts = []) {
  return [...cohorts].sort((a, b) => (a.order ?? 0) - (b.order ?? 0));
} // sortCohortsByOrder()

/**
 * Reindex cohorts order based on array position.
 * @param {CareModelCohort[]} cohorts
 * @returns {CareModelCohort[]}
 */
export function reindexCohorts(cohorts = []) {
  return cohorts.map((cohort, index) => ({
    ...cohort,
    order: index,
  }));
} // reindexCohorts()

/**
 * Normalize cohort data by filling missing fields with defaults.
 * @param {Partial<CareModelCohort>} cohort
 * @param {string} generatedId
 * @param {number} defaultOrder
 * @returns {CareModelCohort}
 */
export function normalizeCohort(cohort, generatedId, defaultOrder = 0) {
  return {
    id: cohort.id || generatedId,
    cohortName: cohort.cohortName || '',
    filters: cohort.filters || {},
    order: cohort.order ?? defaultOrder,
  };
} // normalizeCohort()

/**
 * Return labels from cohort filters.
 * @param {CohortFilters} filters
 * @returns {string[]}
 */
export function getCohortFilterLabels(filters = {}) {
  return Object.values(filters)
    .flatMap((filter) => filter?.filterStatements || [])
    .map((statement) => statement?.label)
    .filter(Boolean);
} // getCohortFilterLabels()

/**
 * Count criteria (filter statements) across all cohort filters.
 * @param {CohortFilters} filters
 * @returns {number}
 */
export function countCohortCriteria(filters = {}) {
  return Object.values(filters || {}).reduce((count, filter) => (
    count + (filter?.filterStatements?.length || 0)
  ), 0);
} // countCohortCriteria()

/**
 * Build assigned clients count by cohort id.
 * @param {Client[]} clients
 * @returns {Record<string, number>}
 */
export function getAssignedClientsCountByCohort(clients = []) {
  return clients.reduce((acc, client) => {
    if (!client.careModelCohort) {
      return acc;
    }

    acc[client.careModelCohort] = (acc[client.careModelCohort] || 0) + 1;
    return acc;
  }, {});
} // getAssignedClientsCountByCohort()

/**
 * Collect the ids of the clients currently assigned to a cohort.
 * @param {string} cohortId
 * @param {Client[]} clients
 * @returns {string[]}
 */
export function getClientIdsAssignedToCohort(cohortId, clients = []) {
  return clients
    .filter((client) => client.careModelCohort === cohortId)
    .map((client) => client.id);
} // getClientIdsAssignedToCohort()

/**
 * Replace or append cohort in collection.
 * @param {CareModelCohort[]} cohorts
 * @param {CareModelCohort} cohort
 * @returns {CareModelCohort[]}
 */
export function upsertCohort(cohorts, cohort) {
  return sortCohortsByOrder([
    ...cohorts.filter((item) => item.id !== cohort.id),
    cohort,
  ]);
} // upsertCohort()

/**
 * Remove cohort from collection.
 * @param {CareModelCohort[]} cohorts
 * @param {string} cohortId
 * @returns {CareModelCohort[]}
 */
export function removeCohort(cohorts, cohortId) {
  return cohorts.filter((item) => item.id !== cohortId);
} // removeCohort()
